// composables/useMapInteractions.ts
import type Interaction from 'ol/interaction/Interaction'
import type Feature from 'ol/Feature'
import Draw, { type Options as DrawOptions } from 'ol/interaction/Draw'
import Select, { type Options as SelectOptions } from 'ol/interaction/Select'
import Modify, { type Options as ModifyOptions } from 'ol/interaction/Modify'
import Snap, { type Options as SnapOptions } from 'ol/interaction/Snap'
import DragPan, { type Options as DragPanOptions } from 'ol/interaction/DragPan'
import Translate, { type Options as TranslateOptions } from 'ol/interaction/Translate'
import GeoJSON from 'ol/format/GeoJSON'
import { useSharedMap } from './use-shared-map'

// JSTS
import { GeoJSONReader, GeoJSONWriter } from 'jsts/org/locationtech/jts/io'
import { GeometryFactory } from 'jsts/org/locationtech/jts/geom'
import UnionOp from 'jsts/org/locationtech/jts/operation/union/UnionOp'
import { DragZoom } from 'ol/interaction'
import { ref, shallowRef } from 'vue'

type InteractionKey = string

const active = new Map<InteractionKey, Interaction>()
const boundaryFeatures = shallowRef<Feature[] | null>(null)

export function useSharedMapInteractions () {
  const { map } = useSharedMap()

  // ---------- core add / remove ----------
  function add (key: InteractionKey, interaction: Interaction) {
    if (!map.value) return
    remove(key)
    map.value.addInteraction(interaction)
    active.set(key, interaction)
  }

  function remove (key: InteractionKey) {
    const interaction = active.get(key)
    if (interaction && map.value) {
      map.value.removeInteraction(interaction)
      active.delete(key)
    }
  }

  function clear () {
    active.forEach((_, key) => remove(key))
  }

  function has (key: InteractionKey) {
    return active.has(key)
  }

  // ---------- convenience helpers ----------
  function enableDraw (options: DrawOptions, key: InteractionKey = 'draw') {
    const draw = new Draw(options)
    add(key, draw)
    return draw
  }

  function enableSelect (options: SelectOptions = {}, key: InteractionKey = 'select') {
    const select = new Select(options)
    add(key, select)
    return select
  }

  function enableModify (options: ModifyOptions, key = 'modify') {
    const modify = new Modify(options)
    add(key, modify)
    return modify
  }

  function enableSnap (options: SnapOptions, key = 'snap') {
    const snap = new Snap(options)
    add(key, snap)
    return snap
  }

  function enableDragPan (options: DragPanOptions, key = 'dragPan') {
    const dragPan = new DragPan(options)
    add(key, dragPan)
    return dragPan
  }

  function enableTranslate (options: TranslateOptions = {}, key = 'translate') {
    const translate = new Translate(options)
    add(key, translate)
    return translate
  }

  // ======================================================
  //  Default Map Interactions
  // ======================================================
  function setMapDragPan (enabled = true) {
    // disable default dragpan
    map.value?.getInteractions().forEach((interaction) => {
      if (interaction instanceof DragPan) {
        interaction.setActive(enabled)
      }
    })
  }

  function setMapDragZoom (enabled = true) {
    // disable default dragpan
    map.value?.getInteractions().forEach((interaction) => {
      if (interaction instanceof DragZoom) {
        interaction.setActive(enabled)
      }
    })
  }

  // ======================================================
  //  GeoJSON + JSTS Union helpers
  // ======================================================

  /**
   * Union all features in a source into a single geometry
   * and return GeoJSON string (or object).
   */
  function featuresToUnionedGeoJSON (
    features: Feature[],
    options: {
      dataProjection?: string
      featureProjection?: string
      asObject?: boolean
    } = {}
  ): string | object | null {
    if (!features.length) return null

    const {
      dataProjection = 'EPSG:3857',
      featureProjection = 'EPSG:3857',
      asObject = false,
    } = options

    const format = new GeoJSON()
    const reader = new GeoJSONReader(new GeometryFactory())
    const writer = new GeoJSONWriter()

    let unioned: any = null

    features.forEach((feature, index) => {
      const olGeom = feature.getGeometry()
      if (!olGeom) return

      // Convert OL geometry → GeoJSON object → JSTS geometry
      const geomObject = format.writeGeometryObject(olGeom, {
        dataProjection,
        featureProjection,
      })

      const jstsGeom = reader.read(geomObject)

      if (index === 0) {
        unioned = jstsGeom
      } else {
        unioned = UnionOp.union(unioned, jstsGeom)
      }
    })

    if (!unioned) return null

    const geojson = writer.write(unioned)

    return asObject ? geojson : JSON.stringify(geojson)
  }

  /**
   * Convenience: get unioned GeoJSON directly from a VectorSource
   */
  function sourceToUnionedGeoJSON (
    source: import('ol/source/Vector').default,
    options?: Parameters<typeof featuresToUnionedGeoJSON>[1]
  ) {
    return featuresToUnionedGeoJSON(source.getFeatures(), options)
  }

  /**
   * Ready-to-use payload helper (matches your current shape)
   */
  function createBoundaryPayload (
    source: import('ol/source/Vector').default,
    crs: string = 'EPSG:3857'
  ) {
    const geoJsonStr = sourceToUnionedGeoJSON(source, {
      dataProjection: crs,
      featureProjection: crs,
    })

    if (!geoJsonStr) return null

    return {
      boundary_geojson: geoJsonStr as string,
      crs,
    }
  }

  function createFeatureBoundaryPayload (
    features: Feature[],
    crs: string = 'EPSG:3857'
  ) {
    const geoJsonStr = featuresToUnionedGeoJSON(features, {
      dataProjection: crs,
      featureProjection: crs,
    })

    if (!geoJsonStr) return null

    return {
      boundary_geojson: geoJsonStr as string,
      crs,
    }
  }

  function createFeatureVolumePayload (
    features: Feature[],
    crs: string = 'EPSG:3857',
    volume_level_method: string = 'bestfit',
    volume_level_value: number = 0,
  ) {
    const geoJsonStr = featuresToUnionedGeoJSON(features, {
      dataProjection: crs,
      featureProjection: crs,
    })

    if (!geoJsonStr) return null

    return {
      boundary_geojson: geoJsonStr as string,
      crs,
      volume_level_method,
      volume_level_value,
    }
  }

  return {
    // core
    add,
    remove,
    clear,
    has,

    // interactions
    enableDraw,
    enableSelect,
    enableModify,
    enableSnap,
    enableDragPan,
    enableTranslate,

    // map interactions
    setMapDragPan,
    setMapDragZoom,

    // geojson / jsts
    featuresToUnionedGeoJSON,
    sourceToUnionedGeoJSON,
    createBoundaryPayload,
    createFeatureBoundaryPayload,
    createFeatureVolumePayload,

    boundaryFeatures,

    active,
  }
}
